<template>
  <div
    id="controllable"
    class="Canvas__Background"
  >
    <div id="threejscontainer" />
    <div
      class="Controls"
      v-if="isDebug"
    >
      <button
        class="button"
        @click="ChangePOV(0)"
        v-show="step === 1 || step === 2"
      >
        Front
      </button>
      <button
        class="button"
        @click="ChangePOV(1)"
        v-show="step === 1 || step === 2"
      >
        Left
      </button>
      <button
        class="button"
        @click="ChangePOV(2)"
        v-show="step === 1 || step === 2"
      >
        Right
      </button>
      <button
        class="button"
        @click="Randomize()"
        v-show="step === 1 || step === 2 || step === 3"
      >
        Randomize!
      </button>
      <button
        class="button"
        @click="GetCode()"
        v-show="step === 1 || step === 2 || step === 3"
      >
        {{ copied ? 'Copied!' : 'Copy code to clipboard' }}
      </button>
      <button
        class="button"
        @click="DownloadMesh()"
        v-show="step === 1 || step === 2 || step === 3"
      >
        {{ downloaded ? 'Downloading..' : 'Download 3D model' }}
      </button>
      <button
        class="button"
        @click="CloseDebug()"
      >
        Close Debug Menu
      </button>
    </div>
    <div
      class="LoaderContainer"
      v-show="(step > 0) && isLoading"
    >
      <div class="sk-circle">
        <div class="sk-circle1 sk-child" />
        <div class="sk-circle2 sk-child" />
        <div class="sk-circle3 sk-child" />
        <div class="sk-circle4 sk-child" />
        <div class="sk-circle5 sk-child" />
        <div class="sk-circle6 sk-child" />
        <div class="sk-circle7 sk-child" />
        <div class="sk-circle8 sk-child" />
        <div class="sk-circle9 sk-child" />
        <div class="sk-circle10 sk-child" />
        <div class="sk-circle11 sk-child" />
        <div class="sk-circle12 sk-child" />
      </div>
    </div>
  </div>
</template>

<script>
// main.js contains the webGL logic
import * as ThreeMain from './threeMain'
import { EventBus } from '@/event-bus.js'
import { mapGetters } from 'vuex'
import { ParseCode } from '@/components/Code/CodeValidator'

let animationId

export default {
  data () {
    return {
      copied: false,
      downloaded: false
    }
  },
  async mounted () {
    this.EventBusRegister()
    this.RegisterEvents()
    await ThreeMain.Init()
    this.UpdateModel()
    this.animate()
  },
  computed: {
    ...mapGetters(['table', 'force01', 'force02', 'force03', 'curve01', 'curve02', 'curve03', 'curve04', 'u1', 'v1', 'u2', 'v2', 'u3', 'v3', 'step', 'isLoading', 'highlightedSlider', 'isDebug'])
  },
  watch: {
    highlightedSlider () {
      ThreeMain.HighlightSlider(this.highlightedSlider)
    }
  },
  methods: {
    EventBusRegister () {
      EventBus.$on('StepChanged', step => {
        ThreeMain.SetStep(step)
      })
      EventBus.$on('TableChanged', table => {
        ThreeMain.LoadTable(table)
        this.copied = false
        this.downloaded = false
      })
      EventBus.$on('PtHighlighted', (index) => {
        ThreeMain.FocusOnPt(index)
      })
      EventBus.$on('StartPtDrag', (index) => {
        ThreeMain.SetDragOn(index)
      })
      EventBus.$on('CurvesChanged', () => {
        this.UpdateModel()
      })
      EventBus.$on('ForcesChanged', () => {
        this.UpdateForces()
      })
      EventBus.$on('PositionsChanged', () => {
        this.UpdateForces()
      })
      EventBus.$on('DebugMode', (debug) => {
        ThreeMain.SetDebugStats(debug)
      })
      EventBus.$on('Zoom', (val) => {
        ThreeMain.Zoom(val)
      })
    },
    RegisterEvents () {
      window.addEventListener('resize', ThreeMain.onWindowResize)
      window.addEventListener('mousemove', ThreeMain.onMouseMove)
      window.addEventListener('mousedown', ThreeMain.HandleRaycastOnce)
      window.addEventListener('mouseup', ThreeMain.EndDrag)
      window.addEventListener('touchend', ThreeMain.EndDrag)
      window.addEventListener('touchmove', ThreeMain.onMouseMove)
      window.addEventListener('touchstart', ThreeMain.HandleRaycastOnce)
    },
    ChangePOV (index) {
      ThreeMain.GoToPOV(index)
    },
    animate: function () {
      animationId = requestAnimationFrame(this.animate)
      ThreeMain.DrawScene()
    },
    UpdateModel () {
      ThreeMain.UpdateShape(this.curve01, this.curve02, this.curve03, this.curve04, this.u1, this.v1, this.u2, this.v2, this.u3, this.v3, this.force01, this.force02, this.force03)
      this.copied = false
      this.downloaded = false
    },
    UpdateForces () {
      ThreeMain.UpdateTexture(this.u1, this.v1, this.u2, this.v2, this.u3, this.v3, this.force01, this.force02, this.force03)
      this.copied = false
      this.downloaded = false
    },
    GetCode () {
      this.copied = true
      const s = ParseCode()
      let res = ''
      for (let i = 0; i < s.length; i++) {
        res += s[i]
        if (i > 0 && (i + 1) % 4 === 0) {
          res += ' '
        }
      }
      navigator.clipboard.writeText(s)
    },
    DownloadMesh () {
      this.downloaded = true
      ThreeMain.DownloadMesh(ParseCode())
    },
    Randomize () {
      this.$store.dispatch('SetSliders', [this.RandomSlider(), this.RandomSlider(), this.RandomSlider(), this.RandomSlider()])
      this.$store.dispatch('SetUVs', [Math.random(), Math.random(), Math.random(), Math.random(), Math.random(), Math.random()])
      this.$store.dispatch('SetForces', [Math.random(), Math.random(), Math.random()])
    },
    RandomSlider () {
      return Math.round((Math.random() * 24) - 12)
    },
    CloseDebug () {
      this.$store.dispatch('SetDebug', false)
    }
  },
  beforeDestroy () {
    cancelAnimationFrame(animationId)
    window.removeEventListener('resize', ThreeMain.onWindowResize)
    window.removeEventListener('mousemove', ThreeMain.onMouseMove)
    window.removeEventListener('mousedown', ThreeMain.HandleRaycastOnce)
    window.removeEventListener('mouseup', ThreeMain.EndDrag)
    window.removeEventListener('touchend', ThreeMain.EndDrag)
    window.removeEventListener('touchmove', ThreeMain.onMouseMove)
    window.removeEventListener('touchstart', ThreeMain.HandleRaycastOnce)
    EventBus.$off('StepChanged')
    EventBus.$off('TableChanged')
    EventBus.$off('PtHighlighted')
    EventBus.$off('StartPtDrag')
    EventBus.$off('CurvesChanged')
    EventBus.$off('ForcesChanged')
    EventBus.$off('PositionsChanged')
    EventBus.$off('DebugMode')
    EventBus.$off('Zoom')
  }
}
</script>

<style scoped>
.Canvas__Background {
  min-width: 100%;
  min-height: 100%;
}

.Controls {
  position: absolute;
  width: 300px;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  margin-left: 1em;
  margin-bottom: 1em;
  overflow: hidden;
  }

.Control{
  margin-bottom: 1em;
  flex-direction: column;
  display: flex;
}

.button {
  background: lightgray;
  color: black;
  height : 2em;
  border: solid white 1px;
  border-radius: 5px;
}

.buttonSelected {
  background: darkgrey;
  color: white;
  height : 2em;
  border: solid white 1px;
  border-radius: 5px;
}

#drawingCanvas {
  position: absolute;
  top: 0;
  visibility: hidden;
}

.LoaderContainer{
  width: 100%;
  height:100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.sk-circle {
  margin: auto;
  width: 40px;
  height: 40px;
  position: relative;
}
.sk-circle .sk-child {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
}
.sk-circle .sk-child:before {
  content: '';
  display: block;
  margin: 0 auto;
  width: 15%;
  height: 15%;
  background-color: white;
  opacity: 0.5;
  border-radius: 100%;
  -webkit-animation: sk-circleBounceDelay 1.2s infinite ease-in-out both;
          animation: sk-circleBounceDelay 1.2s infinite ease-in-out both;
}
.sk-circle .sk-circle2 {
  -webkit-transform: rotate(30deg);
      -ms-transform: rotate(30deg);
          transform: rotate(30deg); }
.sk-circle .sk-circle3 {
  -webkit-transform: rotate(60deg);
      -ms-transform: rotate(60deg);
          transform: rotate(60deg); }
.sk-circle .sk-circle4 {
  -webkit-transform: rotate(90deg);
      -ms-transform: rotate(90deg);
          transform: rotate(90deg); }
.sk-circle .sk-circle5 {
  -webkit-transform: rotate(120deg);
      -ms-transform: rotate(120deg);
          transform: rotate(120deg); }
.sk-circle .sk-circle6 {
  -webkit-transform: rotate(150deg);
      -ms-transform: rotate(150deg);
          transform: rotate(150deg); }
.sk-circle .sk-circle7 {
  -webkit-transform: rotate(180deg);
      -ms-transform: rotate(180deg);
          transform: rotate(180deg); }
.sk-circle .sk-circle8 {
  -webkit-transform: rotate(210deg);
      -ms-transform: rotate(210deg);
          transform: rotate(210deg); }
.sk-circle .sk-circle9 {
  -webkit-transform: rotate(240deg);
      -ms-transform: rotate(240deg);
          transform: rotate(240deg); }
.sk-circle .sk-circle10 {
  -webkit-transform: rotate(270deg);
      -ms-transform: rotate(270deg);
          transform: rotate(270deg); }
.sk-circle .sk-circle11 {
  -webkit-transform: rotate(300deg);
      -ms-transform: rotate(300deg);
          transform: rotate(300deg); }
.sk-circle .sk-circle12 {
  -webkit-transform: rotate(330deg);
      -ms-transform: rotate(330deg);
          transform: rotate(330deg); }
.sk-circle .sk-circle2:before {
  -webkit-animation-delay: -1.1s;
          animation-delay: -1.1s; }
.sk-circle .sk-circle3:before {
  -webkit-animation-delay: -1s;
          animation-delay: -1s; }
.sk-circle .sk-circle4:before {
  -webkit-animation-delay: -0.9s;
          animation-delay: -0.9s; }
.sk-circle .sk-circle5:before {
  -webkit-animation-delay: -0.8s;
          animation-delay: -0.8s; }
.sk-circle .sk-circle6:before {
  -webkit-animation-delay: -0.7s;
          animation-delay: -0.7s; }
.sk-circle .sk-circle7:before {
  -webkit-animation-delay: -0.6s;
          animation-delay: -0.6s; }
.sk-circle .sk-circle8:before {
  -webkit-animation-delay: -0.5s;
          animation-delay: -0.5s; }
.sk-circle .sk-circle9:before {
  -webkit-animation-delay: -0.4s;
          animation-delay: -0.4s; }
.sk-circle .sk-circle10:before {
  -webkit-animation-delay: -0.3s;
          animation-delay: -0.3s; }
.sk-circle .sk-circle11:before {
  -webkit-animation-delay: -0.2s;
          animation-delay: -0.2s; }
.sk-circle .sk-circle12:before {
  -webkit-animation-delay: -0.1s;
          animation-delay: -0.1s; }

@-webkit-keyframes sk-circleBounceDelay {
  0%, 80%, 100% {
    -webkit-transform: scale(0);
            transform: scale(0);
  } 40% {
    -webkit-transform: scale(1);
            transform: scale(1);
  }
}

@keyframes sk-circleBounceDelay {
  0%, 80%, 100% {
    -webkit-transform: scale(0);
            transform: scale(0);
  } 40% {
    -webkit-transform: scale(1);
            transform: scale(1);
  }
}
</style>
